import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputBase from "@material-ui/core/InputBase";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Chip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_TAGS") {
    const tags = action.payload;
    const newTags = [];

    const newState = [...state];

    tags.forEach((tag) => {
      const tagIndex = newState.findIndex((s) => s.id === tag.id);
      if (tagIndex !== -1) {
        newState[tagIndex] = tag;
      } else {
        newTags.push(tag);
      }
    });

    return [...newState, ...newTags];
  }

  if (action.type === "UPDATE_TAGS") {
    const tag = action.payload;
    const tagIndex = state.findIndex((s) => s.id === tag.id);

    if (tagIndex !== -1) {
      const newState = [...state];
      newState[tagIndex] = tag;
      return newState;
    } else {
      return [tag, ...state];
    }
  }

  if (action.type === "DELETE_TAGS") {
    const tagId = action.payload;

    const tagIndex = state.findIndex((s) => s.id === tagId);
    if (tagIndex !== -1) {
      const newState = [...state];
      newState.splice(tagIndex, 1);
      return newState;
    }
    return state;
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor:
      theme.palette.type === "light"
        ? "#f5f5f5"
        : theme.palette.background.default,
    padding: theme.spacing(4),
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontWeight: 800,
    color: theme.palette.primary.main,
    fontSize: "2.125rem",
    lineHeight: 1.235,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "50px",
    padding: "6px 20px",
    width: "300px",
    boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.2)",
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s ease",
    "&:focus-within": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      borderColor: theme.palette.primary.main,
    },
  },
  searchInput: {
    marginLeft: theme.spacing(1),
    flex: 1,
    fontSize: "0.95rem",
  },
  searchIcon: {
    color: theme.palette.text.secondary,
  },
  actionButton: {
    borderRadius: "50px",
    padding: "8px 24px",
    fontWeight: "bold",
    textTransform: "none",
    boxShadow: theme.shadows[3],
    "&:hover": {
      boxShadow: theme.shadows[6],
    },
  },
  paperTable: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    borderRadius: "16px",
    backgroundColor: "transparent",
    boxShadow: "none",
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: "16px",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  },
  cardContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  cardActions: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1, 1, 1),
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: "auto",
  },
}));

const Tags = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTags = async () => {
        try {
          const { data } = await api.get("/tags/", {
            params: { searchParam, pageNumber, kanban: 1 },
          });
          dispatch({ type: "LOAD_TAGS", payload: data.tags });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchTags();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const onTagsEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        if (data.tag && Number(data.tag.kanban) === 1) {
          dispatch({ type: "UPDATE_TAGS", payload: data.tag });
        }
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
      }
    };
    socket.on(`company${user.companyId}-tag`, onTagsEvent);

    return () => {
      socket.off(`company${user.companyId}-tag`, onTagsEvent);
    };
  }, [socket]);

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleReturnToKanban = () => {
    history.push("/kanban");
  };

  return (
    <div className={classes.mainContainer}>
      <ConfirmationModal
        title={
          deletingTag && `${i18n.t("tagsKanban.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tagsKanban.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {tagModalOpen && (
        <TagModal
          open={tagModalOpen}
          onClose={handleCloseTagModal}
          aria-labelledby="form-dialog-title"
          tagId={selectedTag && selectedTag.id}
          kanban={1}
        />
      )}

      <div className={classes.header}>
        <div className={classes.titleContainer}>
          <Typography className={classes.title}>
            {i18n.t("tagsKanban.title")} ({tags.length})
          </Typography>
          <Typography className={classes.subtitle}>
            Gerencie as colunas e status do seu quadro Kanban
          </Typography>
        </div>

        <div className={classes.headerActions}>
          <div className={classes.searchContainer}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              placeholder={i18n.t("contacts.searchPlaceholder")}
              classes={{
                root: classes.searchInput,
              }}
              value={searchParam}
              onChange={handleSearch}
            />
          </div>
          <Button
            variant="contained"
            onClick={handleReturnToKanban}
            className={classes.actionButton}
            startIcon={<ArrowBackIcon />}
            style={{
              backgroundColor: "transparent",
              color: "gray",
              boxShadow: "none",
            }}
          >
            {"Voltar"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenTagModal}
            className={classes.actionButton}
            startIcon={<AddIcon />}
          >
            {i18n.t("tagsKanban.buttons.add")}
          </Button>
        </div>
      </div>

      <div className={classes.paperTable} onScroll={handleScroll}>
        <Grid container spacing={3}>
          {tags.map((tag) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={tag.id}>
              <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                  <Typography
                    variant="h6"
                    component="h2"
                    style={{ fontWeight: "bold" }}
                  >
                    <Chip
                      variant="outlined"
                      style={{
                        backgroundColor: tag.color,
                        textShadow: "1px 1px 1px #000",
                        color: "white",
                        fontWeight: "bold",
                        borderRadius: "8px",
                      }}
                      label={tag.name}
                      size="small"
                    />
                  </Typography>
                  <Typography
                    color="textSecondary"
                    style={{ marginTop: "12px" }}
                  >
                    {i18n.t("tagsKanban.table.tickets")}:{" "}
                    <strong>
                      {tag?.ticketTags ? tag?.ticketTags?.length : 0}
                    </strong>
                  </Typography>
                </CardContent>
                <CardActions className={classes.cardActions}>
                  <IconButton size="small" onClick={() => handleEditTag(tag)}>
                    <EditIcon />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setConfirmModalOpen(true);
                      setDeletingTag(tag);
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
          {loading && (
            <Grid item xs={12}>
              <Typography
                variant="body1"
                color="textSecondary"
                align="center"
                style={{ marginTop: "20px" }}
              >
                Carregando...
              </Typography>
            </Grid>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default Tags;
